import { useState } from 'react'
import { APP_NAME } from '@/config'
import { isIos, isStandalone, promptInstall, usePwa } from '@/lib/pwa'
import { Button } from './Button'
import { CloseIcon } from './icons'
import './InstallHint.css'

const SEEN_KEY = 'cuplzap.installHintSeen'

function seen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1'
  } catch {
    return true
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1')
  } catch {
    // storage blocked: the hint simply shows again next time
  }
}

/**
 * A one-time banner at the top of the Plan tab: on iPhone Safari outside standalone mode, the
 * two steps to add the app to the Home Screen; on Android and desktop Chrome,
 * an Install button when the browser makes the prompt available.
 */
export function InstallHint() {
  const canInstall = usePwa((s) => s.canInstall)
  const [dismissed, setDismissed] = useState(() => seen() || isStandalone())
  if (dismissed) return null
  const ios = isIos()
  if (!ios && !canInstall) return null

  const close = () => {
    markSeen()
    setDismissed(true)
  }

  return (
    <div className="install" role="region" aria-label="Install the app">
      <div className="install__body">
        <p className="install__title">Put {APP_NAME} on your home screen</p>
        {ios ? (
          <p className="install__line">
            Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>. It opens full screen and works offline.
          </p>
        ) : (
          <p className="install__line">It opens in its own window and works offline.</p>
        )}
        {!ios && canInstall && (
          <Button variant="primary" onClick={() => void promptInstall().then((ok) => ok && close())}>
            Install
          </Button>
        )}
      </div>
      <button type="button" className="install__close" aria-label="Dismiss" onClick={close}>
        <CloseIcon size={18} />
      </button>
    </div>
  )
}

/** The Install section in Settings: the button, the steps, or "Installed". */
export function InstallSettings() {
  const canInstall = usePwa((s) => s.canInstall)
  if (isStandalone()) return <p className="settings__line">Installed on this device.</p>
  if (isIos()) {
    return (
      <p className="settings__line">
        In Safari, tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>. Notifications on iPhone work only from the installed app.
      </p>
    )
  }
  if (canInstall) {
    return (
      <Button variant="primary" onClick={() => void promptInstall()}>
        Install {APP_NAME}
      </Button>
    )
  }
  return <p className="settings__line">Your browser will offer to install the app from its menu once it has visited a few times, or use its "Install app" option.</p>
}
